/**
 * 기업 구매·좌석 게이트 다이얼로그 문구. web(스토어 구매 게이트)과 console-web(좌석
 * 증감 게이트)이 같은 문구를 쓴다 — 한쪽만 고치면 같은 상황에서 두 앱이 다른 말을 한다.
 *
 * `@platform/ui` 를 import 하지 않는다(계약 패키지가 UI 패키지에 의존하면 안 된다).
 * 형태 검증은 `showAlert`/`showConfirm` 호출부에서 `OverlayOptions` 로 걸린다.
 */
export interface DialogCopy {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}
export const BusinessConfirm = {
  loginRequired: {
    title: '로그인하시겠어요?',
    message: '기업 상품 구매는 로그인이 필요합니다.',
    confirmText: '로그인',
  },
  /**
   * 개인 계정이 기업 상품을 사려 할 때.
   *
   * '전환'을 안내하지 않는다 — 개인 계정은 기업 계정이 될 수 없다.
   * 기업으로 쓰려면 기업 회원가입으로 새 계정을 만들어야 한다.
   */
  businessSignupRequired: {
    title: '기업 회원가입이 필요해요',
    message:
      '기업 상품은 기업 계정으로만 구매할 수 있습니다.\n개인 계정은 기업 계정으로 전환되지 않으니 기업 회원가입을 진행해 주세요.',
    confirmText: '개인플랜 보기',
    cancelText: '취소',
  },
  /**
   * 기업 계정인데 소속 조직이 없을 때.
   * 조직은 국세청 검증을 통과한 기업 회원가입 시점에 서버가 만든다 —
   * 화면에서 만들 수 있는 경로는 없다(검증되지 않은 사업자번호 선점 방지).
   * 새 모델에선 계정이 조직과 함께 태어나므로, 이 상태는 조직이 해산됐거나
   * 구성원에서 제외된 계정에서만 나온다.
   */
  orgNotProvisioned: {
    title: '소속된 기업이 없어요',
    message:
      '기업 콘솔이 준비되지 않았습니다. 이메일 인증을 마치지 않았거나, 조직에서 제외된 계정일 수 있습니다.\n\n소속 기업의 관리자에게 문의해 주세요.',
    confirmText: '마이페이지로 이동',
    cancelText: '취소',
  },
} as const satisfies Record<string, DialogCopy>;

export const BusinessAlert = {
  verificationRequired: {
    title: '기업 검증이 필요해요',
    message:
      '사업자등록증을 첨부해 기업 검증을 완료해야 구매할 수 있습니다. 검증 페이지로 이동합니다.',
    confirmText: '확인',
  },
  verificationPending: {
    title: '검증 심사 중입니다',
    message:
      '제출하신 서류를 검토 중입니다. 승인이 완료되면 구매를 진행할 수 있어요.',
    confirmText: '확인',
  },
  purchaseFailed: {
    title: '구매 진행 실패',
    message: '구매 진행 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.',
    confirmText: '확인',
  },
  /** 콘솔 좌석 증감 게이트 — 해당 조직의 ACTIVE 멤버가 아닐 때. */
  orgMembershipRequired: {
    title: '조직 소속이 확인되지 않았어요',
    message:
      '이 조직의 구성원만 라이선스 수량을 변경할 수 있습니다. 페이지를 새로고침한 뒤 다시 시도해주세요.',
    confirmText: '확인',
  },
  /** 콘솔 좌석 증감 게이트 — 멤버지만 PURCHASE_LICENSE 권한이 없을 때. */
  seatPermissionRequired: {
    title: '권한이 부족해요',
    message:
      '라이선스 수량 변경은 소유자·관리자만 할 수 있습니다. 조직 관리자에게 라이선스 배정을 요청해 주세요.',
    confirmText: '확인',
  },
  /**
   * 기업 계정이 개인 상품을 사려 할 때.
   *
   * 서버도 같은 판정을 하지만(구독 생성 진입점의 fail-closed 게이트), 결제 직전에
   * 400 으로 튕기는 것보다 스토어에서 미리 알려주는 게 낫다.
   */
  individualPurchaseBlocked: {
    title: '기업 회원은 개인 상품을 구매할 수 없어요',
    message:
      '기업 계정의 라이선스는 기업 콘솔에서 기업 상품으로 구매·배정합니다.\n구매 권한이 없다면 소속 기업의 관리자에게 라이선스 배정을 요청해 주세요.',
    confirmText: '확인',
  },
} as const satisfies Record<string, DialogCopy>;
